///
/// EarthBrowser
///
/// Created by Matt Giger on 10/23/2026
/// Copyright (c) 2026 Geodata Labs
///
/// Globe-visibility state and toggle for any docked browser window, shared
/// by the bottom drawer tabs and the right dock rail/header. Dataset
/// windows derive from the datasetWindowMeta registry; dropped-file
/// windows toggle their own isVisible flag; GeoPackage windows toggle the
/// active layer. Windows with nothing on the globe (terminals, file
/// browsers) report `undefined` and get no toggle.
///

import { updateDatasetWindow, type DatasetBrowserWindowState } from "@/redux/dialogSlice"
import { sendDatasetAction, setGeoPackageLayerVisible } from "@/redux/earthBrowserSlice"
import { useAppDispatch, useAppSelector } from "@/redux/store"
import { useCallback } from "react"
import { cDatasetWindowMeta } from "./datasetWindowMeta"

export function useDockWindowVisibility() {
	const dispatch = useAppDispatch()
	const visibleDatasets = useAppSelector(state => state.earthbrowser.visibleDatasets)
	const geopackageLayerVisibility = useAppSelector(state => state.earthbrowser.geopackageLayerVisibility)

	const getVisibility = useCallback((windowState: DatasetBrowserWindowState): boolean | undefined => {
		const datasetType = windowState.windowType
			? cDatasetWindowMeta[windowState.windowType]?.datasetType
			: undefined
		if (datasetType) {
			return visibleDatasets.includes(datasetType)
		}
		switch (windowState.windowType) {
			case "csv":
			case "shapefile":
			case "geojson":
				return windowState.isVisible
			case "geopackage":
				if (windowState.catalogId && windowState.activeLayerName) {
					const layerVis = geopackageLayerVisibility?.[windowState.catalogId]
					return layerVis?.[windowState.activeLayerName] ?? true
				}
				return windowState.isVisible
			default:
				return undefined
		}
	}, [visibleDatasets, geopackageLayerVisibility])

	const getToggle = useCallback((windowState: DatasetBrowserWindowState): (() => void) | undefined => {
		const datasetType = windowState.windowType
			? cDatasetWindowMeta[windowState.windowType]?.datasetType
			: undefined
		if (datasetType) {
			return () => {
				const newVisibility = !visibleDatasets.includes(datasetType)
				dispatch(sendDatasetAction({ type: datasetType, action: newVisibility ? "enable" : "disable" }))
			}
		}
		switch (windowState.windowType) {
			case "csv":
			case "shapefile":
			case "geojson":
				return () => {
					dispatch(updateDatasetWindow({
						id: windowState.id,
						updates: { isVisible: !windowState.isVisible }
					}))
				}
			case "geopackage": {
				const { catalogId, activeLayerName } = windowState
				if (catalogId && activeLayerName) {
					return () => {
						const currentVis = geopackageLayerVisibility?.[catalogId]?.[activeLayerName] ?? true
						dispatch(setGeoPackageLayerVisible({
							packageId: catalogId,
							layerName: activeLayerName,
							visible: !currentVis,
						}))
					}
				}
				return undefined
			}
			default:
				return undefined
		}
	}, [dispatch, visibleDatasets, geopackageLayerVisibility])

	return { getVisibility, getToggle }
}
